import React, {useState} from 'react';
import {Card, Form, Input, Button, Typography, message} from 'antd';
import {UserOutlined, LockOutlined} from '@ant-design/icons';
import {Link, useNavigate, useLocation, type Location} from 'react-router-dom';
import {authApi} from '@/api/auth';
import type {LocationState} from '@/types/common/Location.types';
import type {AuthPayload} from '@/types/auth.types';
import {
	ContainerCard_BodyStyles,
	ContainerCardStyles,
	ContainerStyles,
	LoginBtnStyles,
	RegistrationRedirectStyles,
	TitleContainerStyles,
	TitleStyles,
} from './Login.styles';
import {emailRules, passwordRules} from './Login.rules';
import {getRedirectPath} from './Login.helpers';

const {Title, Text} = Typography;

const Login: React.FC = () => {
	const navigate = useNavigate();
	const location: Location<LocationState | null> = useLocation();
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);

	const onFinish = async (values: AuthPayload) => {
		setLoading(true);

		try {
			const {data, error, status} = await authApi.login({
				email: values.email,
				password: values.password,
			});

			if (error) {
				message.error(error.message || 'Ошибка входа');
				return;
			}

			if (status === 200 && data) {
				message.success('Вход выполнен');

				const from = location.state;
				const redirectPath = getRedirectPath(from?.from);

				if (redirectPath) {
					const fullFrom = from?.from;

					navigate(
						{
							pathname: redirectPath,
							search: fullFrom?.search ?? '',
							hash: fullFrom?.hash ?? '',
						},
						{replace: true}
					);
				} else {
					navigate('/', {replace: true});
				}
			}
		} finally {
			setLoading(false);
		}
	};

	return (
		<div style={ContainerStyles}>
			<Card style={ContainerCardStyles} styles={{body: ContainerCard_BodyStyles}}>
				<div style={TitleContainerStyles}>
					<Title level={3} style={TitleStyles}>
						Вход
					</Title>
					<Text type="secondary">Введите данные для входа в приложение</Text>
				</div>
				<Form
					form={form}
					name="login"
					layout="vertical"
					size="large"
					onFinish={onFinish}
					requiredMark={false}
				>
					<Form.Item name="email" label="Email" rules={emailRules}>
						<Input prefix={<UserOutlined />} placeholder="user@example.com" />
					</Form.Item>
					<Form.Item name="password" label="Пароль" rules={passwordRules}>
						<Input.Password prefix={<LockOutlined />} placeholder="Пароль" />
					</Form.Item>
					<Form.Item style={LoginBtnStyles}>
						<Button
							type="primary"
							htmlType="submit"
							block
							size="large"
							loading={loading}
						>
							Войти
						</Button>
					</Form.Item>
					<div style={RegistrationRedirectStyles}>
						<Text type="secondary">
							Нет аккаунта? <Link to="/register">Зарегистрироваться</Link>
						</Text>
					</div>
				</Form>
			</Card>
		</div>
	);
};

export default Login;
